import { pick, zip } from 'lodash-es'
import { minutesToMilliseconds } from 'date-fns'
import type { UseQueryOptions } from '@tanstack/vue-query'
import { useQuery } from '@tanstack/vue-query'
import { useQueryParams } from './api-utils'
import type {
  SearchApiParams,
  SearchApiResult,
  SearchApiSuggestionsResponse,
} from './search-api.types'

type SearchQueryOptions<R = SearchResult> = Partial<
  Omit<UseQueryOptions<R>, 'select'>
> & {
  enabled?: MaybeRefOrGetter<boolean | undefined>
  select?(result: SearchResult, params: SearchApiParams): R
}

export function useSearchQuery<R = SearchResult>(
  query: MaybeRefOrGetter<SearchApiParams>,
  options: SearchQueryOptions<R> = {},
) {
  const fetch = useRequestFetch()
  const context = useSearchContext()
  const l10n = useL10N()

  const params = computed(() =>
    sortKeys({ language: l10n.language, ...toValue(query) }),
  )

  return useQuery({
    ...(options as Omit<typeof options, 'enabled' | 'select'>),
    placeholderData: (prev: SearchApiResult | undefined) => prev, // Keep previous data between requests
    enabled: computed(
      () => toValue(options.enabled) !== false && import.meta.client,
    ),
    queryKey: ['search-api', 'accomtrips', params] as const,
    queryFn({ queryKey: [_, __, q], signal }) {
      return fetch<SearchApiResult>('/search-api/v1/engines/accomtrips', {
        query: mapSearchApiParams(q),
        signal,
      })
    },
    select(result) {
      const mappedResult = mapSearchResult(context, result, toValue(params), {
        facets: false, // do not map facets for legacy search, since they are mapped in filters
      })

      return options.select
        ? options.select(mappedResult, toValue(query))
        : mappedResult
    },
  })
}

type SearchMultiQuery = {
  requests: SearchApiParams[]
  language?: string
}

export function useSearchMultiQuery<R = SearchResult[]>(
  query: MaybeRefOrGetter<SearchMultiQuery>,
  options: {
    enabled?: MaybeRefOrGetter<boolean>
    select?(result: SearchResult[], params: SearchMultiQuery): R
  } = {},
) {
  const fetch = useRequestFetch()
  const context = useSearchContext()

  const params = computed(() => ({
    requests: toValue(query).requests.map(sortKeys),
    language: toValue(query).language,
  }))

  return useQuery({
    enabled: toRef(options.enabled),
    queryKey: ['search-api', 'multi', params] as const,
    queryFn({ queryKey: [_, __, { requests, language }], signal }) {
      return fetch<SearchApiResult[]>(
        '/search-api/v1/engines/accomtrips/multi',
        {
          query: { request: requests.map(stringifyParams) },
          headers: removeFalsy({ 'Accept-Language': language }),
          signal,
        },
      )
    },
    select(results) {
      const queryValue = toValue(query)
      const mappedResults = results.map((result, index) =>
        mapSearchResult(context, result, queryValue.requests[index]),
      )

      return options.select
        ? options.select(mappedResults, queryValue)
        : mappedResults
    },
  })
}

type SearchAlternativeKey = 'sameRegion' | 'extendedDates'

const SEARCH_ALTERNATIVE_COUNT_MAX = 10
const SEARCH_ALTERNATIVE_FUZZINESS = 3

/**
 * Searches for alternatives to a query with zero results.
 *
 * @param query The original search query for which alternatives are to be found.
 */
export function useSearchAlternativesQuery(
  query: MaybeRefOrGetter<SearchApiParams>,
) {
  const confData = useConfdata()
  const season = useSearchSeason(query)
  const isFreeweek = useFlag('freeweek')

  const requests = computed(() => {
    const queryBase = {
      currency: confData.currency,
      salesoffice: confData.salesoffice?.code,
      season: season.value,
      ...toValue(query),
      pagesize: SEARCH_ALTERNATIVE_COUNT_MAX,
    }

    return removeFalsy({
      sameRegion: !!queryBase.place && {
        ...queryBase,
        place: undefined,
      },
      extendedDates: hasDateParams(queryBase) && {
        ...queryBase,
        ...addDatesFuzziness(queryBase, SEARCH_ALTERNATIVE_FUZZINESS),
      },
    }) satisfies Record<SearchAlternativeKey, SearchApiParams>
  })

  const params = computed(() => ({
    requests: Object.values(requests.value),
    language: confData.language,
  }))

  return useSearchMultiQuery(params, {
    /**
     * Disabling search for alternatives for Freeweek.
     *
     * @see https://jira.migros.net/browse/IHGWEBCC-2156
     */
    enabled: () => !isFreeweek.value,
    select(results) {
      return zip(
        Object.keys(requests.value),
        Object.values(requests.value),
        results,
      )
        .filter(([, , result]) => result?.docs.length)
        .slice(0, 1)
        .map(([key, request, result]) => ({
          key: key as SearchAlternativeKey,
          destination: getDestinationForFilter(confData.destination, request!),
          request: request!,
          result: result!,
        }))
    },
  })
}

export function useSearchAccommodationsQuery(
  query: MaybeRefOrGetter<SearchApiParams>,
  options: Omit<SearchQueryOptions, 'select'> = {},
) {
  return useSearchQuery(query, {
    ...options,
    select(result) {
      return result
    },
  })
}

export function useSearchRegionPlacesQuery(
  query: MaybeRefOrGetter<
    Pick<SearchApiParams, 'country' | 'region' | 'facet'>
  >,
  options: { enabled?: MaybeRefOrGetter<boolean | undefined> } = {},
) {
  return useSearchAccommodationsQuery(
    computed(() => ({
      ...pick(toValue(query), ['country', 'region', 'facet']),
      pagesize: SEARCH_API_PAGESIZE_NONE,
    })),
    options,
  )
}

const SUGGESTIONS_FUZZINESS_DEFAULT = 5
const SUGGESTIONS_STALE_TIME_MINUTES = 20

export function useSearchApiSuggestionsQuery(query: MaybeRefOrGetter<string>) {
  const fetch = useRequestFetch()

  const confData = useConfdata()
  const settings = useSettings()

  const params = useQueryParams(() => ({
    type: settings.autosuggestSearchtype,
    source: settings.autosuggestSource,
    size: settings.autosuggestSize,
    fuzziness: SUGGESTIONS_FUZZINESS_DEFAULT,
    language: confData.language,
    locale: confData.locale,
    query: toValue(query),
  }))

  const isQueryValid = computed(() => {
    const { length } = toValue(query)

    return !length || length >= settings.autosuggestThreshold
  })

  return useQuery({
    staleTime: minutesToMilliseconds(SUGGESTIONS_STALE_TIME_MINUTES),
    enabled: isQueryValid,
    placeholderData(previousData?: SearchApiSuggestionsResponse) {
      return isQueryValid.value ? previousData : undefined
    },
    queryKey: ['search-suggestions', params] as const,
    queryFn({ queryKey: [_, queryParams], signal }) {
      return fetch<SearchApiSuggestionsResponse>('/search-api/v1/suggest', {
        query: queryParams,
        signal,
      })
    },
    select(data) {
      return flatSuggestions(data?.Suggestions ?? []).map(normalizeSuggestion)
    },
  })
}

export function useAccomVacanciesQuery(
  code: MaybeRefOrGetter<string>,
  trip: MaybeRefOrGetter<Partial<TripParams>>,
) {
  const fetch = useRequestFetch()

  const runtimeConfig = useRuntimeConfig()
  const confData = useConfdata()

  const isFreeweek = useFlag('freeweek')

  const params = useQueryParams(() => ({
    maxage: runtimeConfig.public.maxage.vacancies,
    currency: confData.currency,
    freeweek: isFreeweek.value || undefined,
    // The pax is critical because search-api returns different min price
    // depending on whether it is specified or not.
    pax: toValue(trip).pax,
  }))

  return useQuery({
    queryKey: [`accom-vacancies`, code, params] as const,
    placeholderData: keepPreviousDataPlaceholder,
    queryFn({ queryKey: [_, id, query] }) {
      return fetch<Trips>(`/search-api/v1/accoms/${id}/vacancies`, { query })
    },
  })
}

/**
 * Converts params to a multi-search request string.
 *
 * @param params Request params.
 * @returns String in format `country:AT,pets:true,sorting:-averagerating`.
 */
function stringifyParams(params: object): string {
  return JSON.stringify(params).replace(/["{}]/g, '')
}
